import React, { useState, useEffect } from 'react';
import { losApi, LOAN_STATUS_LABELS, PARTY_ROLE_LABELS } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { isSpendApprovalError } from '../../api';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Sparkles, Loader2, Copy } from 'lucide-react';
import { ModuleHeader } from '../shared/formFields';
import { copyText } from '../../clipboard';
import { formatDateTime } from '../../kit/time';

export function AiProcessorTab({ loan, onChange }) {
  const [packages, setPackages] = useState([]);
  const [running, setRunning] = useState(false);
  const [context, setContext] = useState('');
  const load = () => losApi.listAiPackages(loan.loan_file_id).then(setPackages);
  useEffect(() => { load(); }, [loan.loan_file_id]);
  const run = async () => {
    setRunning(true);
    try {
      const res = await losApi.runAiProcessor(loan.loan_file_id, { additional_context: context });
      const n = res.suggestions_created;
      const dupes = res.duplicates_skipped ? ` · ${res.duplicates_skipped} already on the file, skipped` : '';
      toast({
        title: 'AI Processor complete',
        description: `${n} suggested condition${n === 1 ? '' : 's'} to review on the Conditions tab · ${res.party_summaries_count} messages${dupes}`,
      });
      setContext(''); load(); onChange();
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({ title: 'AI failed', description: e.response?.data?.detail?.substring(0, 200) || 'Error' });
    } finally { setRunning(false); }
  };
  return (
    <div className="space-y-4">
      <ModuleHeader title="AI Loan Processor" subtitle="Claude suggests program-appropriate conditions for you to accept, plus party-specific warm messages to copy" />
      <div className="rounded-2xl p-6 border shadow-sm relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', borderColor: '#4f46e5' }}>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0 text-white">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/80">
              <Sparkles size={12} /> AI Loan Processor
            </div>
            <div className="font-serif text-2xl font-bold mt-1">Generate conditions + party messages</div>
            <p className="text-white/70 text-sm mt-2 max-w-xl">Runs Claude across the loan file to produce a program-appropriate condition list and warm, plain-English messages for each party.</p>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-white/70">Current Status</div>
            <div className="font-serif text-lg font-bold">{LOAN_STATUS_LABELS[loan.status]}</div>
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-xs text-white/80">Optional context</Label>
          <Textarea rows={2} value={context} onChange={(e) => setContext(e.target.value)}
            className="mt-1 bg-white/10 border-white/20 text-white placeholder-white/50"
            placeholder="e.g., Borrower is self-employed, gift funds from parents…"
            data-testid="ai-context-input" />
        </div>
        <button onClick={run} disabled={running} data-testid="run-ai-processor-btn"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-white text-purple-700 hover:bg-white/95">
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? 'Generating…' : 'Run AI Processor'}
        </button>
      </div>
      {packages.map((pkg) => <PackageCard key={pkg.package_id} pkg={pkg} />)}
      {packages.length === 0 && (
        <div className="bg-white rounded-2xl p-8 border border-dashed border-slate-200 text-center text-sm text-slate-400">
          No AI packages generated yet. Click "Run AI Processor" to create one.
        </div>
      )}
    </div>
  );
}

function PackageCard({ pkg }) {
  const [expanded, setExpanded] = useState(true);
  const roles = Object.keys(pkg.party_summaries || {});
  return (
    <div className="bg-white rounded-xl p-5 border border-slate-200">
      <div className="flex justify-between items-start flex-wrap gap-2">
        <div>
          <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>Package · {formatDateTime(pkg.generated_at)}</div>
          <div className="text-[11px] text-slate-500 mt-1">
            {(pkg.suggestion_ids || pkg.condition_ids || []).length} suggested condition{(pkg.suggestion_ids || pkg.condition_ids || []).length !== 1 ? 's' : ''} · {roles.length} party message{roles.length !== 1 ? 's' : ''} · triggered on {pkg.trigger_status}
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>
      {expanded && (
        <div className="mt-4 space-y-3">
          {roles.map((role) => (
            <PartyMessageCard key={role} role={role} message={pkg.party_summaries[role]} pkgId={pkg.package_id} />
          ))}
        </div>
      )}
    </div>
  );
}

function PartyMessageCard({ role, message, pkgId }) {
  // Nothing here sends anything. Staff copy the message and send it themselves (ticket 192, D23);
  // the copy is logged on the package, best effort, so the file shows it was used.
  const copy = async () => {
    if (await copyText(message)) {
      toast({ title: `Copied ${role} message` });
      losApi.markDelivered(pkgId, { party_id: role, channel: 'copy' }).catch(() => {});
    } else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };
  return (
    <div className="rounded-lg border border-slate-100 p-4 bg-slate-50/50" data-testid={`party-message-${role}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#4c7f21' }}>
          {PARTY_ROLE_LABELS[role] || role}
        </div>
        <div className="flex gap-1">
          <button onClick={copy} data-testid={`copy-message-${role}`} className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-white inline-flex items-center gap-1"><Copy size={10} /> Copy</button>
        </div>
      </div>
      <div className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{message}</div>
    </div>
  );
}
